import { useState, useCallback } from "react";
import { Plus } from "lucide-react";
import { Badge } from "@/components/atoms/Badge";
import { branchesService } from "@/services/branchesService";
import { formatRelativeTime } from "@/lib/format";
import { SimpleListPage } from "@/modules/_shared/SimpleListPage";
import { CreateBranchModal } from "./CreateBranchModal";
import type { Column } from "@/components/organisms/DataTable";
import type { BranchFilter, BranchSummary } from "@/types/entities";

const columns: Column<BranchSummary>[] = [
  {
    id: "name",
    header: "Branch",
    sortable: true,
    cell: (r) => (
      <div className="min-w-0">
        <div className="truncate font-medium text-bridge-fg">{r.name ?? "(unnamed)"}</div>
        <div className="truncate text-xs text-bridge-muted">{r.sourceCrm} · {r.externalId}</div>
      </div>
    ),
  },
  {
    id: "town",
    header: "Location",
    sortable: true,
    cell: (r) => (
      <div className="text-sm text-bridge-fg">
        {r.town ?? "—"}
        <div className="text-xs text-bridge-muted">{r.postcode ?? ""}</div>
      </div>
    ),
  },
  {
    id: "status",
    header: "Status",
    cell: (r) =>
      r.status === "ACTIVE" ? <Badge tone="success">Active</Badge>
      : r.status === "INACTIVE" ? <Badge tone="neutral">Inactive</Badge>
      : r.status ? <Badge>{r.status}</Badge>
      : <span className="text-bridge-subtle">—</span>,
  },
  {
    id: "atlasModifiedAt",
    header: "Updated",
    sortable: true,
    align: "right",
    cell: (r) => <span className="text-bridge-muted">{formatRelativeTime(r.atlasModifiedAt)}</span>,
  },
];

export function BranchesPage() {
  const [createOpen, setCreateOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleCreated = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  return (
    <>
      <SimpleListPage<BranchSummary, BranchFilter>
        key={refreshKey}
        title="Branches"
        fetcher={branchesService.list}
        columns={columns}
        rowKey={(r) => r.id}
        searchPlaceholder="Search by name, town, postcode..."
        emptyTitle="No branches match"
        showPageLoader
        toolbar={() => (
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-white bg-bridge-accent hover:bg-bridge-accentSoft rounded-lg shadow-sm transition-all"
          >
            <Plus size={14} />
            Create Branch
          </button>
        )}
      />
      <CreateBranchModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={handleCreated}
      />
    </>
  );
}